// src/protocols/vop.js — протоколи ВОП по чл. 117 + импорт от Excel (Фаза 3.2).

import { el, clear, $, field, input, row, clickableRow } from '../shared/dom.js';
import { numberLine } from '../shared/docNumber.js';
import { dateInput, isoToBg, localTodayIso, guardDates } from '../shared/dates.js';
import { money, dfmt } from '../shared/format.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { emptyState, docNo, actionsMenu } from '../shared/ui.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { auditBlock } from '../shared/audit.js';
import { viesButton } from '../shared/vies.js';
import { xlsxDownload } from '../shared/xlsx.js';
import { bulkManager } from '../shared/pickers.js';
import { impPick, impNum, impReadFile, parseDateCell } from '../shared/importers.js';
import { state, cq, contacts as contactsCache, isAdminUser } from '../shared/state.js';
import { kontoAllowed, exportDialog as kontoExportDialog, settingsDialog as kontoSettingsDialog } from './konto.js';
import { sortTh, sortLabel } from '../shared/sortHeader.js';
import { regHead, regFilters, regField, regCount, REG_TABLE } from '../shared/register.js';

function supplierPicker(contacts, f) {
  const search = f.supplier_name;
  const list = el('div', { style: 'display:none;position:absolute;z-index:60;left:0;right:0;background:var(--surface,#fff);border:1px solid var(--border);border-radius:8px;max-height:240px;overflow:auto;box-shadow:0 8px 24px rgba(0,0,0,.15)' });
  function fill(c) {
    if (c) {
      f.supplier_name.value = c.name || '';
      const vat = c.vat_number || c.eik || '';
      if (vat) f.supplier_vat.value = vat;
      if (c.country && f.supplier_country && !f.supplier_country.value) f.supplier_country.value = c.country;
      if (c.id != null && f.contact_id && !f.contact_id.value) f.contact_id.value = String(c.id);
    }
    list.style.display = 'none';
  }
  function render(q) {
    clear(list);
    const ql = (q || '').toLowerCase().trim();
    const items = contacts.filter((c) => !ql || ((c.name || '') + ' ' + (c.eik || '') + ' ' + (c.vat_number || '')).toLowerCase().includes(ql)).slice(0, 60);
    items.forEach((c) => list.appendChild(el('div', { style: 'padding:7px 10px;cursor:pointer', onMouseDown: (e) => { e.preventDefault(); fill(c); }, onMouseOver: (e) => { e.currentTarget.style.background = 'var(--surface-2,#f3f4f6)'; }, onMouseOut: (e) => { e.currentTarget.style.background = ''; } },
      el('div', { style: 'font-weight:600', text: c.name || '' }),
      (c.eik || c.vat_number) ? el('div', { style: 'font-size:12px;color:var(--muted)', text: [c.eik ? 'ЕИК: ' + c.eik : '', c.vat_number ? 'ДДС: ' + c.vat_number : ''].filter(Boolean).join('  ') }) : null)));
    if (!items.length) list.appendChild(el('div', { style: 'padding:8px 10px;color:var(--muted)', text: 'Няма съвпадение' }));
    list.style.display = '';
  }
  search.addEventListener('focus', () => render(search.value));
  search.addEventListener('input', () => render(search.value));
  search.addEventListener('blur', () => setTimeout(() => { list.style.display = 'none'; }, 150));
  return el('div', { style: 'position:relative' }, search, list);
}

// Масови действия (само админ): избор на редове + триене, и по избор смяна на статус.

export async function view(host) {
  clear(host);
  let protocols = [], contacts = [];
  // Контактите са помощни: отказът им (напр. без право) не бива да изпразва
  // самия списък — грешка от картата, 16.10.2026 (тогава Promise.all падаше
  // целият и екранът казваше „Още няма …").
  const [prR, conR] = await Promise.allSettled([api('/protocols' + cq()), api('/contacts' + cq())]);
  if (prR.status === 'fulfilled') protocols = prR.value || []; else toast(prR.reason.message, true);
  if (conR.status === 'fulfilled') contacts = conR.value || [];

  const panel = el('div', { class: 'panel' });
  const bulk = bulkManager({ endpoint: '/protocols', refresh: () => window.CRM.renderView($('#page')), label: 'протокола' });
  // Видът е като в „Доставки" (Макс, 29.10.2026): заглавието вляво, бутоните
  // вдясно на един ред, „+ Нов протокол" — последен. Конто-бутоните са видими
  // (ползват се всеки месец), импортът и образецът — в „Още ▾". Експортът към
  // Конто и настройките му са само за кантората (сървърът отказва същото).
  const konto = kontoAllowed() ? [
    el('button', { class: 'btn sm ghost', type: 'button', text: 'Настройки за Конто…', onClick: () => kontoSettingsDialog(protocols) }),
    el('button', { class: 'btn sm ghost', type: 'button', text: 'Експорт към Конто 6…', onClick: () => kontoExportDialog(protocols) }),
  ] : [];
  const more = actionsMenu('Още', [
    { label: 'Импорт от Excel', onClick: () => importProtocolsXlsx() },
    { label: 'Свали празен Excel образец', onClick: () => downloadProtocolTemplate() },
  ]);
  const pHead = regHead('Протоколи по чл. 117 (ВОП)', [...konto, more],
    el('button', { class: 'btn sm', type: 'button', text: '+ Нов протокол', onClick: () => protocolForm(null, contacts) }),
    bulk.toolbar);
  panel.appendChild(pHead);
  if (!protocols.length) { panel.appendChild(emptyState('▤', 'Още няма протоколи.', '+ Нов протокол', () => protocolForm(null, contacts))); host.appendChild(panel); return; }

  const flt = { q: '', year: '', sortKey: 'number', sortDir: 'desc' };
  const years = [...new Set(protocols.map((p) => (p.protocol_date || '').slice(0, 4)).filter(Boolean))].sort().reverse();
  const search = input({ type: 'search', placeholder: 'Търси № или доставчик…', 'aria-label': 'Търси № или доставчик' });
  search.addEventListener('input', () => { flt.q = search.value.toLowerCase(); draw(); });
  const yearSel = el('select', { 'aria-label': 'Година', onChange: (e) => { flt.year = e.target.value; draw(); } }, el('option', { value: '', text: 'Всички години' }), ...years.map((y) => el('option', { value: y, text: y })));
  // Филтрите с надписи отгоре, броячът — над таблицата (като в „Доставки").
  panel.appendChild(regFilters(regField('№ или доставчик', search, 'l'), regField('Година', yearSel)));
  const countLbl = regCount();
  panel.appendChild(countLbl);

  const numOf = (p) => parseInt(String(p.number || '').replace(/\D/g, ''), 10) || 0;
  function setSort(k) { if (flt.sortKey === k) flt.sortDir = flt.sortDir === 'asc' ? 'desc' : 'asc'; else { flt.sortKey = k; flt.sortDir = 'asc'; } draw(); }
  const thNo = sortTh({}, () => setSort('number'));
  const thSup = sortTh({}, () => setSort('supplier'));
  const thDt = sortTh({ class: 'hide-sm' }, () => setSort('date'));
  const thBase = sortTh({ class: 'num' }, () => setSort('base'));
  const thVat = el('th', { scope: 'col', class: 'num' });
  // „Конто": отметка, че протоколът вече е влязъл във файл за Конто (само
  // отметка — редакцията остава възможна). Клиентът не изнася и не я вижда.
  const thKonto = kontoAllowed() ? el('th', { scope: 'col', class: 'hide-sm', text: 'Конто' }) : null;
  const tb = el('tbody');
  function draw() {
    bulk.reset();
    clear(tb);
    let rows = protocols.filter((p) => {
      if (flt.year && (p.protocol_date || '').slice(0, 4) !== flt.year) return false;
      if (flt.q) { const hay = ((p.number || '') + ' ' + (p.supplier_name || '')).toLowerCase(); if (!hay.includes(flt.q)) return false; }
      return true;
    });
    const dir = flt.sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (flt.sortKey === 'supplier') { const x = (a.supplier_name || '').toLowerCase(), y = (b.supplier_name || '').toLowerCase(); return x < y ? -dir : x > y ? dir : 0; }
      if (flt.sortKey === 'date') { const x = a.protocol_date || '', y = b.protocol_date || ''; return x < y ? -dir : x > y ? dir : 0; }
      if (flt.sortKey === 'base') return ((Number(a.tax_base) || 0) - (Number(b.tax_base) || 0)) * dir;
      return (numOf(a) - numOf(b)) * dir;
    });
    rows.forEach((pr) => {
      tb.appendChild(clickableRow(() => protocolForm(pr, contacts), 'Отвори протокол № ' + pr.number,
        bulk.cell(pr.id, 'протокол № ' + pr.number),
        el('td', { style: 'font-weight:600' }, '№ ', docNo(pr.number)),
        el('td', {}, pr.supplier_name || '—'),
        el('td', { class: 'hide-sm' }, dfmt(pr.protocol_date)),
        el('td', { class: 'num' }, money(pr.tax_base, pr.currency)),
        el('td', { class: 'num' }, money(pr.vat_amount, pr.currency)),
        thKonto ? el('td', { class: 'hide-sm' }, pr.konto_exported_at ? el('span', { class: 'tag green', title: 'Изнесен към Конто на ' + dfmt(String(pr.konto_exported_at).slice(0, 10)), text: '✓ изнесен' }) : el('span', { class: 'muted', text: '—' })) : null
      ));
    });
    sortLabel(thNo, '№', flt.sortKey === 'number' ? flt.sortDir : ''); sortLabel(thSup, 'Доставчик (ЕС)', flt.sortKey === 'supplier' ? flt.sortDir : ''); sortLabel(thDt, 'Дата', flt.sortKey === 'date' ? flt.sortDir : ''); sortLabel(thBase, 'Данъчна основа', flt.sortKey === 'base' ? flt.sortDir : ''); thVat.textContent = 'ДДС';
    countLbl.set(rows.length, protocols.length, flt.q);
  }
  panel.appendChild(el('table', { class: REG_TABLE }, el('thead', {}, el('tr', {}, bulk.headTh, thNo, thSup, thDt, thBase, thVat, thKonto)), tb));
  draw();
  host.appendChild(panel);
}

export function protocolForm(existing, contacts) {
  if (!contacts) contacts = contactsCache();
  const pr = existing || {};
  const f = {
    contact_id: el('input', { type: 'hidden', value: pr.contact_id ? String(pr.contact_id) : '' }),
    protocol_date: dateInput(pr.protocol_date || localTodayIso()),
    tax_event_date: dateInput(pr.tax_event_date),
    supplier_name: input({ type: 'search', value: pr.supplier_name || '', placeholder: 'Търси по име, ЕИК или ДДС №…', autocomplete: 'off' }),
    supplier_vat: input({ value: pr.supplier_vat || '', placeholder: 'напр. DE123456789' }),
    supplier_country: input({ value: pr.supplier_country || '', placeholder: 'напр. Германия' }),
    supplier_invoice_number: input({ value: pr.supplier_invoice_number || '' }),
    supplier_invoice_date: dateInput(pr.supplier_invoice_date),
    description: el('textarea', { placeholder: 'Предмет на доставката…' }, pr.description || ''),
    // „Само евро" (01.09.2026): менюто за валута падна; скритото поле пази
    // валутата на заварен протокол, за да не я подмени записът.
    currency: el('input', { type: 'hidden', value: pr.currency || 'EUR' }),
    tax_base: input({ type: 'number', step: '0.01', value: pr.tax_base != null ? pr.tax_base : '' }),
    vat_rate: input({ type: 'number', step: '0.01', value: pr.vat_rate != null ? pr.vat_rate : 20 }),
    legal_ground: input({ value: pr.legal_ground || 'чл. 84 от ЗДДС (ВОП)' }),
    notes: el('textarea', { placeholder: 'Бележки…' }, pr.notes || ''),
  };

  const vatBox = el('div', { class: 'totals' });
  function recompute() {
    const base = Number(f.tax_base.value) || 0;
    const rate = Number(f.vat_rate.value) || 0;
    const vat = base * rate / 100;
    clear(vatBox);
    vatBox.appendChild(el('div', { class: 'line' }, el('span', { text: 'Данъчна основа' }), el('span', { text: money(base, f.currency.value) })));
    vatBox.appendChild(el('div', { class: 'line grand' }, el('span', { text: 'Начислен ДДС ' + rate + '%' }), el('span', { text: money(vat, f.currency.value) })));
  }
  f.tax_base.addEventListener('input', recompute);
  f.vat_rate.addEventListener('input', recompute);
  recompute();

  // Заварен протокол в друга валута я пази — бележката обяснява разликата.
  const curNote = (pr.currency && pr.currency !== 'EUR')
    ? el('div', { class: 'cal-hint', style: 'padding:0 0 8px', text: 'Заварен документ във валута ' + pr.currency + ' — запазва се както е издаден.' })
    : null;

  // „Клиент (по избор)" падна (ПРОМТ-18): протоколът е към доставчик от ЕС,
  // полето само объркваше. contact_id остава скрито — избраният доставчик го
  // пълни (supplierPicker), а заварените стойности не се губят.
  const body = el('div', {},
    numberLine('/protocols', 'Новият протокол', existing),
    f.contact_id, f.currency, curNote,
    row(field('Дата на протокол', f.protocol_date), field('Дата на данъчно събитие', f.tax_event_date)),
    el('h4', { text: 'Доставчик (ЕС)', style: 'margin:10px 0 6px' }),
    row(field('Наименование', supplierPicker(contacts, f)), field('ДДС № (ЕС)', f.supplier_vat)),
    el('div', { style: 'margin:-2px 0 10px' }, viesButton(() => f.supplier_vat.value, (r) => {
      if (!f.supplier_name.value && r.name) f.supplier_name.value = r.name;
    })),
    row(field('Държава', f.supplier_country), field('Фактура №', f.supplier_invoice_number)),
    field('Дата на фактурата', f.supplier_invoice_date),
    field('Предмет на доставката', f.description),
    row(field('Данъчна основа', f.tax_base), field('ДДС %', f.vat_rate)),
    vatBox,
    field('Правно основание', f.legal_ground),
    field('Бележки', f.notes)
  );

  const save = async (close) => {
    // Неразбрана дата не тръгва празна — сървърът би сложил днешна (ПРОМТ-19).
    if (!guardDates(f.protocol_date)) return null;
    const p = { company_id: state().activeCompany };
    for (const k in f) p[k] = ('iso' in f[k].dataset) ? f[k].dataset.iso : f[k].value;
    if (!p.supplier_name) { toast('Въведете доставчик.', true); return null; }
    try {
      let saved;
      if (existing && existing.id) saved = await api('/protocols/' + existing.id, { method: 'PUT', body: JSON.stringify(p) });
      else saved = await api('/protocols', { method: 'POST', body: JSON.stringify(p) });
      toast('Записано — протокол № ' + saved.number); if (close) closeModal(); window.CRM.renderView($('#page')); return saved;
    } catch (e) { toast(e.message, true); return null; }
  };

  const foot = el('div', { style: 'flex-wrap:wrap' });
  if (existing && existing.id) {
    // „Създай подобен" (левият ъгъл — както „Копирай" при фактурите): отваря
    // НОВ протокол с доставчика, предмета и сумите на този. Без id/номер —
    // номерът идва от брояча чак при „Запази". Датите и фактурата на
    // доставчика НЕ се пренасят: те са реквизити на конкретната стара
    // доставка и оставени наготово биха влезли в новия документ незабелязано.
    foot.appendChild(el('button', { class: 'btn sm ghost copy-btn', text: 'Създай подобен', title: 'Нов протокол със същия доставчик и данни', style: 'margin-right:auto', onClick: () => {
      closeModal();
      protocolForm({
        contact_id: existing.contact_id,
        supplier_name: existing.supplier_name,
        supplier_vat: existing.supplier_vat,
        supplier_country: existing.supplier_country,
        description: existing.description,
        currency: existing.currency,
        tax_base: existing.tax_base,
        vat_rate: existing.vat_rate,
        legal_ground: existing.legal_ground,
        notes: existing.notes,
      }, contacts);
    } }));
    foot.appendChild(el('button', { class: 'btn sm ghost', text: 'PDF', onClick: () => window.open('/api/protocols/' + existing.id + '/pdf', '_blank') }));
    if (isAdminUser()) foot.appendChild(el('button', { class: 'btn sm danger', text: 'Изтрий', onClick: async () => { if (await confirmDialog('Да изтрия ли този протокол?', { okText: 'Изтрий', danger: true })) { try { await api('/protocols/' + existing.id, { method: 'DELETE' }); closeModal(); toast('Изтрито.'); window.CRM.renderView($('#page')); } catch (e) { toast('Изтриването не се получи. ' + e.message, true); } } } }));
  }
  foot.appendChild(el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }));
  foot.appendChild(el('button', { class: 'btn', text: 'Запази', onClick: () => save(true) }));
  if (existing && existing.id) { const _ab = auditBlock(existing); if (_ab) body.appendChild(_ab); }
  openModal({ title: existing && existing.id ? 'Протокол № ' + existing.number : 'Нов протокол', body, foot, wide: true });
}

async function importProtocolsXlsx() {
  if (!state().activeCompany) { toast('Първо изберете фирма.', true); return; }
  impReadFile(async (rows) => {
    let ok = 0, failed = 0;
    for (const r of rows) {
      const supplier = impPick(r, ['Доставчик', 'supplier_name']);
      if (!supplier) { failed++; continue; }
      const payload = {
        company_id: state().activeCompany,
        number: impPick(r, ['№ на документ', '№ Протокол', 'Протокол №', 'Номер', '№', 'number']),
        protocol_date: parseDateCell(r['Дата на протокол']),
        tax_event_date: parseDateCell(r['Дата на данъчно събитие']),
        supplier_name: supplier,
        supplier_vat: impPick(r, ['ДДС № (ЕС)', 'ДДС номер', 'supplier_vat']),
        supplier_country: impPick(r, ['Държава', 'supplier_country']),
        supplier_invoice_number: impPick(r, ['Фактура №', 'supplier_invoice_number']),
        supplier_invoice_date: parseDateCell(r['Дата на фактурата']),
        description: impPick(r, ['Предмет', 'Описание', 'description']),
        currency: impPick(r, ['Валута', 'currency']) || 'EUR',
        tax_base: impNum(impPick(r, ['Данъчна основа', 'tax_base']), 0),
        vat_rate: impNum(impPick(r, ['ДДС %', 'vat_rate']), 20),
        legal_ground: impPick(r, ['Правно основание', 'legal_ground']),
        notes: impPick(r, ['Бележки', 'notes']),
      };
      try { await api('/protocols', { method: 'POST', body: JSON.stringify(payload) }); ok++; } catch (e) { failed++; }
    }
    toast('Импортирани протоколи: ' + ok + (failed ? (', пропуснати: ' + failed) : ''));
    if (state().view === 'protocols') window.CRM.renderView($('#page'));
  });
}

function downloadProtocolTemplate() {
  const data = [{
    '№ на документ': '',
    'Дата на протокол': isoToBg(localTodayIso()), 'Дата на данъчно събитие': '',
    'Доставчик': 'EU Supplier GmbH', 'ДДС № (ЕС)': 'DE123456789', 'Държава': 'Германия',
    'Фактура №': 'INV-001', 'Дата на фактурата': '', 'Предмет': 'Стоки по ВОП',
    'Валута': 'EUR', 'Данъчна основа': 1000, 'ДДС %': 20,
    'Правно основание': 'чл. 84 от ЗДДС (ВОП)', 'Бележки': '',
  }];
  xlsxDownload(data, 'Protokoli', 'maket-protokoli.xlsx');
}

